import React, { useState, useEffect, useCallback } from 'react';
import { motion, Reorder } from 'framer-motion';
import { Row, Col, Typography, Input, Button, Popconfirm } from 'antd';
import { DragOutlined, SaveOutlined, DeleteOutlined, PlusOutlined } from '@ant-design/icons';
import './DraggableDescriptors.css';
import OrderingAPI from '../../utils/OrderingAPI';

const { Text } = Typography;
const { TextArea } = Input;

const DraggableDescriptors = ({ 
    category, 
    isEditing, 
    onDescriptorChange, 
    onDescriptorAdd,
    onDescriptorDelete,
    onOrderChange,
    onSaveOrder
}) => {
    const [items, setItems] = useState([]);
    const [hasChanges, setHasChanges] = useState(false);

    useEffect(() => {
        // Convertir descriptores a formato de items con índices
        const descriptorItems = category.descriptors
            .map((descriptor, index) => ({
                id: `desc_${category.category_id}_${index}`, // ID más estable
                index: index,
                value: descriptor || '',
                position: index + 1
            }))
            .filter(item => item.value.trim() !== ''); // Solo mostrar descriptores no vacíos
        
        setItems(descriptorItems);
    }, [category]);

    const handleReorder = async (newItems) => {
        setItems(newItems);
        
        // Guardar automáticamente el nuevo orden
        try {
            const newOrder = newItems.map(item => item.position);
            
            if (onOrderChange) {
                onOrderChange(category.category_id, newOrder);
            }
            
            // Guardar automáticamente
            if (onSaveOrder) {
                await onSaveOrder(category.category_id, newOrder);
            } else {
                // Usar la API directamente si no hay función onSaveOrder
                await OrderingAPI.updateDescriptorOrder(category.category_id, newOrder);
            }
            
            console.log('Orden guardado automáticamente');
        } catch (error) {
            console.error('Error al guardar orden automáticamente:', error);
            setHasChanges(true); // Si hay error, mostrar botón para guardar manual
        }
    };

    const handleDescriptorChange = useCallback((itemId, newValue) => {
        const updatedItems = items.map(item => 
            item.id === itemId ? { ...item, value: newValue } : item
        );
        setItems(updatedItems);
        
        // Actualizar en el componente padre
        const itemIndex = items.findIndex(item => item.id === itemId);
        if (itemIndex !== -1 && onDescriptorChange) {
            onDescriptorChange(category.category_id, items[itemIndex].index, newValue);
        }
    }, [items, onDescriptorChange, category.category_id]);

    const handleAddDescriptor = () => {
        const newIndex = items.length;
        const newDescriptor = {
            id: `desc_${category.category_id}_${newIndex}`,
            index: newIndex,
            value: '',
            position: newIndex + 1
        };
        
        const updatedItems = [...items, newDescriptor];
        setItems(updatedItems);
        
        if (onDescriptorAdd) {
            onDescriptorAdd('');
        }
    };

    const handleDeleteDescriptor = (itemId) => {
        const itemIndex = items.findIndex(item => item.id === itemId);
        if (itemIndex === -1) return;

        // Encontrar el índice original en los descriptores de la categoría
        const originalIndex = items[itemIndex].index;
        
        const updatedItems = items
            .filter(item => item.id !== itemId)
            .map((item, index) => ({ ...item, position: index + 1 }));
        
        setItems(updatedItems);
        setHasChanges(true);
        
        if (onDescriptorDelete && originalIndex !== undefined) {
            onDescriptorDelete(originalIndex);
        }
    };

    const saveOrder = async () => {
        try {
            const newOrder = items.map(item => item.position);
            
            if (onSaveOrder) {
                await onSaveOrder(category.category_id, newOrder);
            } else {
                // Usar la API directamente si no hay función onSaveOrder
                await OrderingAPI.updateDescriptorOrder(category.category_id, newOrder);
            }
            
            setHasChanges(false);
        } catch (error) {
            console.error('Error al guardar orden:', error);
        }
    };

    if (!isEditing) {
        // Modo solo lectura
        return (
            <div>
                {category.descriptors.map((descriptor, index) => {
                    // Validar que el descriptor tenga contenido
                    const descriptorText = descriptor || '';
                    
                    return descriptorText && descriptorText.trim() !== '' && (
                        <Row key={index} align="middle" style={{ marginBottom: 10 }}>
                            <Col span={4}>
                                <Text>Descriptor {index + 1}:</Text>
                            </Col>
                            <Col span={20}>
                                <Text>{descriptorText}</Text>
                            </Col>
                        </Row>
                    );
                })}
            </div>
        );
    }

    return (
        <div className="draggable-descriptors">
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
                <Text strong>Arrastra para reordenar descriptores:</Text>
                <div>
                    <Button 
                        type="dashed" 
                        size="small" 
                        icon={<PlusOutlined />}
                        onClick={handleAddDescriptor}
                        style={{ marginRight: 8 }}
                    >
                        Agregar
                    </Button>
                    {hasChanges && (
                        <Button 
                            type="primary" 
                            size="small" 
                            icon={<SaveOutlined />}
                            onClick={saveOrder}
                        >
                            Guardar Orden
                        </Button>
                    )}
                </div>
            </div>
            
            <Reorder.Group 
                axis="y" 
                values={items} 
                onReorder={handleReorder}
                className="reorder-group"
            >
                {items.map((item) => (
                    <Reorder.Item 
                        key={item.id} 
                        value={item}
                        className="reorder-item"
                        whileDrag={{ scale: 1.02, zIndex: 1000 }}
                        dragElastic={0.1}
                    >
                        <Row align="middle" style={{ width: '100%' }}>
                            <Col span={1} className="drag-handle">
                                <DragOutlined style={{ color: '#00b7a3', cursor: 'grab' }} />
                            </Col>
                            <Col span={4} style={{ display: 'flex', alignItems: 'center', height: '100%' }}>
                                <Text>Descriptor {item.position}:</Text>
                            </Col>
                            <Col span={17}>
                                <TextArea
                                    autoSize={{ minRows: 2, maxRows: 4 }}
                                    value={item.value}
                                    onChange={(e) => handleDescriptorChange(item.id, e.target.value)}
                                    placeholder={`Ingrese el descriptor ${item.position}`}
                                />
                            </Col>
                            <Col span={2} style={{ textAlign: 'center' }}>
                                <Popconfirm
                                    title="¿Eliminar descriptor?"
                                    description="Esta acción no se puede deshacer"
                                    onConfirm={() => handleDeleteDescriptor(item.id)}
                                    okText="Sí"
                                    cancelText="No"
                                >
                                    <Button 
                                        danger 
                                        size="small" 
                                        icon={<DeleteOutlined />}
                                        style={{ marginTop: 4 }}
                                    />
                                </Popconfirm>
                            </Col>
                        </Row>
                    </Reorder.Item>
                ))}
            </Reorder.Group>
            
            {items.length === 0 && (
                <div style={{ textAlign: 'center', padding: '20px', color: '#999' }}>
                    <Text type="secondary">No hay descriptores. Haz clic en "Agregar" para crear el primero.</Text>
                </div>
            )}
        </div>
    );
};

export default DraggableDescriptors;